import { useEffect, useRef, useState, type FormEvent } from "react";
import { apiRequest } from "../api";
import { Dialog, Field, StatusPill } from "../components";
import type {
  AdminSupportTicketListResponse,
  SupportTicketStatus,
  SupportTicketThreadResponse,
} from "../types";
import { appendUniqueSupportTickets } from "./support-ticket-list";
import { useAdminUrlState } from "./admin-navigation";
import { AdminCustomerLinks } from "./AdminCustomerLinks";

const STATUS_OPTIONS: Array<{ value: SupportTicketStatus; label: string }> = [
  { value: "WAITING_FOR_SUPPORT", label: "等待客服" },
  { value: "WAITING_FOR_USER", label: "等待用户" },
  { value: "CLOSED", label: "已关闭" },
];

type RetryTarget = "queue" | "thread" | null;

type TicketAction = "close" | "reopen";

interface TicketActionDialogTarget {
  action: TicketAction;
  ticketId: string;
  selectionVersion: number;
  subject: string;
  status: SupportTicketStatus;
  queueStatus: SupportTicketStatus;
}

const DEFAULT_REOPEN_REASON = "用户需要继续跟进该事项";

interface SupportInboxProps {
  waitingCount: number;
  onChanged?: () => Promise<void> | void;
}

export function SupportInbox({ waitingCount, onChanged }: SupportInboxProps) {
  const [status, setStatus] = useAdminUrlState<SupportTicketStatus>("support.status", "WAITING_FOR_SUPPORT");
  const [ownerUserId, setOwnerUserId] = useAdminUrlState("support.owner", "");
  const [tickets, setTickets] = useState<AdminSupportTicketListResponse["tickets"]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useAdminUrlState<string | null>("support.selected", null);
  const [thread, setThread] = useState<SupportTicketThreadResponse | null>(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [ticketActionDialog, setTicketActionDialog] =
    useState<TicketActionDialogTarget | null>(null);
  const [reopenReason, setReopenReason] = useState(DEFAULT_REOPEN_REASON);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadReloadVersion, setThreadReloadVersion] = useState(0);
  const [error, setError] = useState("");
  const [retryTarget, setRetryTarget] = useState<RetryTarget>(null);
  const statusRef = useRef(status);
  const selectedIdRef = useRef(selectedId);
  const selectionVersion = useRef(0);
  const queueRequestVersion = useRef(0);
  const threadRequestVersion = useRef(0);
  const loadingMoreCursor = useRef<string | null>(null);

  const clearError = () => {
    setError("");
    setRetryTarget(null);
  };

  const clearThreadContext = (ticketId: string | null) => {
    selectionVersion.current += 1;
    threadRequestVersion.current += 1;
    selectedIdRef.current = ticketId;
    setSelectedId(ticketId);
    setThread(null);
    setReply("");
    setThreadLoading(ticketId !== null);
  };

  const loadQueue = async (
    statusKey: SupportTicketStatus,
    cursor?: string,
    append = false,
  ) => {
    if (append && (!cursor || loadingMoreCursor.current === cursor)) {
      return;
    }
    const requestVersion = ++queueRequestVersion.current;
    if (append) {
      loadingMoreCursor.current = cursor ?? null;
      setLoadingMore(true);
    } else {
      loadingMoreCursor.current = null;
      setLoadingMore(false);
      setLoading(true);
    }
    clearError();
    try {
      const query = new URLSearchParams({ status: statusKey });
      if (ownerUserId) query.set("ownerUserId", ownerUserId);
      if (cursor) {
        query.set("cursor", cursor);
      }
      const result = await apiRequest<AdminSupportTicketListResponse>(
        `/api/admin/support-tickets?${query.toString()}`,
      );
      if (
        requestVersion !== queueRequestVersion.current ||
        statusRef.current !== statusKey
      ) {
        return;
      }
      setTickets((current) =>
        append ? appendUniqueSupportTickets(current, result.tickets) : result.tickets,
      );
      setNextCursor(result.nextCursor);
      if (!append) {
        const selectedStillInQueue =
          selectedIdRef.current !== null &&
          result.tickets.some((ticket) => ticket.id === selectedIdRef.current);
        const nextSelectedId = selectedStillInQueue
          ? selectedIdRef.current
          : result.tickets[0]?.id ?? null;
        if (nextSelectedId !== selectedIdRef.current) {
          clearThreadContext(nextSelectedId);
        }
      }
    } catch (caught) {
      if (
        requestVersion === queueRequestVersion.current &&
        statusRef.current === statusKey
      ) {
        setError(caught instanceof Error ? caught.message : "无法读取客服队列");
        setRetryTarget("queue");
      }
    } finally {
      if (
        requestVersion === queueRequestVersion.current &&
        statusRef.current === statusKey
      ) {
        if (append) {
          setLoadingMore(false);
          loadingMoreCursor.current = null;
        } else {
          setLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    statusRef.current = status;
    queueRequestVersion.current += 1;
    loadingMoreCursor.current = null;
    setTickets([]);
    setNextCursor(null);
    void loadQueue(status);
  }, [status, ownerUserId]);

  const selectStatus = (nextStatus: SupportTicketStatus) => {
    if (nextStatus === statusRef.current) {
      return;
    }
    clearError();
    statusRef.current = nextStatus;
    queueRequestVersion.current += 1;
    loadingMoreCursor.current = null;
    setLoadingMore(false);
    clearThreadContext(null);
    setStatus(nextStatus);
  };

  const selectTicket = (ticketId: string) => {
    if (ticketId === selectedIdRef.current) {
      return;
    }
    clearError();
    clearThreadContext(ticketId);
  };

  useEffect(() => {
    const requestVersion = ++threadRequestVersion.current;
    const ticketId = selectedId;
    const statusAtRequest = status;
    if (!ticketId) {
      setThread(null);
      setThreadLoading(false);
      return;
    }
    let active = true;
    setThread(null);
    setReply("");
    setThreadLoading(true);
    void apiRequest<SupportTicketThreadResponse>(
      `/api/admin/support-tickets/${ticketId}`,
    )
      .then((result) => {
        if (
          !active ||
          requestVersion !== threadRequestVersion.current ||
          statusRef.current !== statusAtRequest ||
          selectedIdRef.current !== ticketId ||
          result.ticket.id !== ticketId
        ) {
          return;
        }
        setThread(result);
      })
      .catch((caught) => {
        if (
          active &&
          requestVersion === threadRequestVersion.current &&
          statusRef.current === statusAtRequest &&
          selectedIdRef.current === ticketId
        ) {
          setError(caught instanceof Error ? caught.message : "无法读取客服线程");
          setRetryTarget("thread");
        }
      })
      .finally(() => {
        if (
          active &&
          requestVersion === threadRequestVersion.current &&
          statusRef.current === statusAtRequest &&
          selectedIdRef.current === ticketId
        ) {
          setThreadLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [selectedId, status, threadReloadVersion]);

  const retry = () => {
    clearError();
    if (retryTarget === "thread" && selectedIdRef.current) {
      setThreadReloadVersion((current) => current + 1);
      return;
    }
    void loadQueue(statusRef.current);
  };

  const sendReply = async (event: FormEvent) => {
    event.preventDefault();
    const operationTicketId = selectedIdRef.current;
    const operationStatus = statusRef.current;
    const operationVersion = selectionVersion.current;
    if (!operationTicketId || threadLoading) {
      return;
    }
    setBusy(true);
    clearError();
    try {
      const result = await apiRequest<SupportTicketThreadResponse>(
        `/api/admin/support-tickets/${operationTicketId}/messages`,
        { method: "POST", body: JSON.stringify({ body: reply }) },
      );
      if (
        statusRef.current === operationStatus &&
        selectedIdRef.current === operationTicketId &&
        selectionVersion.current === operationVersion &&
        result.ticket.id === operationTicketId
      ) {
        setThread(result);
        setReply("");
      }
      await loadQueue(statusRef.current);
      await onChanged?.();
    } catch (caught) {
      if (
        statusRef.current === operationStatus &&
        selectedIdRef.current === operationTicketId &&
        selectionVersion.current === operationVersion
      ) {
        setError(caught instanceof Error ? caught.message : "无法发送客服回复");
      }
    } finally {
      setBusy(false);
    }
  };

  const openTicketActionDialog = (action: TicketAction) => {
    const operationTicketId = selectedIdRef.current;
    if (!operationTicketId || threadLoading || busy || !thread) {
      return;
    }
    setTicketActionDialog({
      action,
      ticketId: operationTicketId,
      selectionVersion: selectionVersion.current,
      subject: thread.ticket.subject,
      status: thread.ticket.status,
      queueStatus: statusRef.current,
    });
    if (action === "reopen") {
      setReopenReason(DEFAULT_REOPEN_REASON);
    }
  };

  const closeTicketActionDialog = () => {
    if (!busy) {
      setTicketActionDialog(null);
    }
  };

  const close = async (target: TicketActionDialogTarget) => {
    if (busy) {
      return;
    }
    setBusy(true);
    clearError();
    try {
      await apiRequest(`/api/admin/support-tickets/${target.ticketId}/close`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      await loadQueue(statusRef.current);
      await onChanged?.();
      if (
        statusRef.current === target.queueStatus &&
        selectedIdRef.current === target.ticketId &&
        selectionVersion.current === target.selectionVersion
      ) {
        setThreadReloadVersion((current) => current + 1);
      }
    } catch (caught) {
      if (statusRef.current === target.queueStatus && selectionVersion.current === target.selectionVersion) {
        setError(caught instanceof Error ? caught.message : "无法关闭客服工单");
      }
    } finally {
      setBusy(false);
      setTicketActionDialog(null);
    }
  };

  const reopen = async (target: TicketActionDialogTarget) => {
    const reason = reopenReason.trim();
    if (busy || !reason) {
      return;
    }
    setBusy(true);
    clearError();
    try {
      await apiRequest(`/api/admin/support-tickets/${target.ticketId}/reopen`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
      await loadQueue(statusRef.current);
      await onChanged?.();
      if (
        statusRef.current === target.queueStatus &&
        selectedIdRef.current === target.ticketId &&
        selectionVersion.current === target.selectionVersion
      ) {
        setThreadReloadVersion((current) => current + 1);
      }
    } catch (caught) {
      if (statusRef.current === target.queueStatus && selectionVersion.current === target.selectionVersion) {
        setError(caught instanceof Error ? caught.message : "无法重开客服工单");
      }
    } finally {
      setBusy(false);
      setTicketActionDialog(null);
    }
  };

  const confirmTicketAction = () => {
    const target = ticketActionDialog;
    if (!target) {
      return;
    }
    if (target.action === "close") {
      void close(target);
      return;
    }
    void reopen(target);
  };

  return (
    <article className="dashboard-card support-inbox-card">
      <div className="card-title-row support-inbox-heading">
        <div><span className="card-kicker">客服</span><h2>工单队列</h2><p className="admin-card-note">按等待时间排序。打开工单后查看完整对话。</p>
          {ownerUserId ? <p>正在查看用户 {ownerUserId} 的工单。<button type="button" className="secondary-button" onClick={() => setOwnerUserId("")}>显示全部用户</button></p> : null}</div>
        <StatusPill tone={waitingCount ? "warn" : "good"}>{waitingCount} 条等待客服</StatusPill>
      </div>
      {error ? <div className="form-error" role="alert">{error}{retryTarget ? <button className="text-button" onClick={retry} type="button">重试加载</button> : null}</div> : null}
      <div className="support-inbox-filters" role="tablist" aria-label="工单状态筛选">
        {STATUS_OPTIONS.map((option) => <button aria-controls="support-inbox-status-panel" aria-selected={status === option.value} className={status === option.value ? "active" : ""} id={`support-inbox-status-${option.value}`} key={option.value} onClick={() => selectStatus(option.value)} onKeyDown={(event) => {
          if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) {
            return;
          }
          const currentIndex = STATUS_OPTIONS.findIndex(({ value }) => value === option.value);
          const nextIndex = event.key === "ArrowRight"
            ? (currentIndex + 1) % STATUS_OPTIONS.length
            : event.key === "ArrowLeft"
              ? (currentIndex - 1 + STATUS_OPTIONS.length) % STATUS_OPTIONS.length
              : event.key === "Home"
                ? 0
                : STATUS_OPTIONS.length - 1;
          event.preventDefault();
          const nextOption = STATUS_OPTIONS[nextIndex];
          selectStatus(nextOption.value);
          document.getElementById(`support-inbox-status-${nextOption.value}`)?.focus();
        }} role="tab" tabIndex={status === option.value ? 0 : -1} type="button">{option.label}</button>)}
      </div>
      {loading ? <div className="support-loading">正在读取队列…</div> : null}
      <div aria-labelledby={`support-inbox-status-${status}`} className="support-inbox-layout" id="support-inbox-status-panel" role="tabpanel">
        <div className="support-inbox-list">
          {tickets.map((ticket) => <button aria-current={selectedId === ticket.id ? "page" : undefined} className={`support-inbox-item ${selectedId === ticket.id ? "active" : ""}`} disabled={busy} key={ticket.id} onClick={() => selectTicket(ticket.id)} type="button"><strong>{ticket.subject}</strong><small>{ticket.owner.username ?? ticket.owner.id} · {formatDate(ticket.lastMessageAt)}</small></button>)}
          {!tickets.length && !loading ? <p className="support-empty">当前筛选没有工单。</p> : null}
          {nextCursor ? <button className="secondary-button support-more" disabled={busy || loading || loadingMore} onClick={() => void loadQueue(statusRef.current, nextCursor, true)} type="button">{loadingMore ? "正在加载…" : "加载更多"}</button> : null}
        </div>
        <div className="support-inbox-thread">
          {threadLoading ? <div className="support-thread-loading" aria-busy="true" aria-live="polite">正在加载工单线程…</div> : thread ? <>
            <div className="support-thread-heading">
              <div><span className="card-kicker">{thread.owner?.username ?? thread.owner?.id}</span><h3>{thread.ticket.subject}</h3><small>{thread.owner?.createdAt ? `账号创建于 ${formatDate(thread.owner.createdAt)}` : ""}</small>
                {thread.owner ? <AdminCustomerLinks userId={thread.owner.id} username={thread.owner.username} /> : null}</div>
              <div className="support-thread-actions"><StatusPill tone={thread.ticket.status === "CLOSED" ? "neutral" : "good"}>{statusLabel(thread.ticket.status)}</StatusPill>{thread.ticket.status === "CLOSED" ? <button className="secondary-button" disabled={busy || threadLoading} onClick={() => openTicketActionDialog("reopen")} type="button">重开</button> : <button className="secondary-button" disabled={busy || threadLoading} onClick={() => openTicketActionDialog("close")} type="button">关闭</button>}</div>
            </div>
            <div className="support-message-list">{thread.messages.map((message) => <div className={`support-message ${message.authorType === "ADMIN" ? "admin" : "user"}`} key={message.id}><div className="support-message-meta"><strong>{message.authorType === "ADMIN" ? "客服" : "用户"}</strong><time dateTime={message.createdAt}>{formatDate(message.createdAt)}</time></div><p className="support-message-body">{message.body}</p></div>)}</div>
            {thread.ticket.status !== "CLOSED" ? <form className="support-reply-form" onSubmit={sendReply}><label className="field"><span className="field-label">回复用户</span><textarea disabled={busy || threadLoading} onChange={(event) => setReply(event.target.value)} required rows={4} value={reply} /></label><button className="button" disabled={busy || threadLoading || !reply} type="submit">{busy ? "发送中…" : "发送回复"}</button></form> : null}
          </> : <p className="support-empty">选择左侧工单查看线程。</p>}
        </div>
      </div>
      <Dialog
        description={
          ticketActionDialog
            ? ticketActionDialog.action === "close"
              ? `将关闭“${ticketActionDialog.subject}”。当前状态：${statusLabel(ticketActionDialog.status)}。关闭后用户无法继续在此线程回复。`
              : `将重开“${ticketActionDialog.subject}”。当前状态：${statusLabel(ticketActionDialog.status)}。重开后工单会回到等待客服队列，并记录以下审计原因。`
            : undefined
        }
        dismissible={!busy}
        footer={
          <>
            <button
              className="button"
              disabled={busy || (ticketActionDialog?.action === "reopen" && !reopenReason.trim())}
              onClick={confirmTicketAction}
              type="button"
            >
              {busy ? "正在提交…" : ticketActionDialog?.action === "reopen" ? "确认重开工单" : "确认关闭工单"}
            </button>
            <button className="secondary-button" disabled={busy} onClick={closeTicketActionDialog} type="button">取消</button>
          </>
        }
        onClose={closeTicketActionDialog}
        open={ticketActionDialog !== null}
        title={ticketActionDialog?.action === "reopen" ? "重开这条工单？" : "关闭这条工单？"}
      >
        {ticketActionDialog?.action === "reopen" ? (
          <Field label="重开原因" hint="必填，将写入工单审计记录。">
            <textarea
              disabled={busy}
              onChange={(event) => setReopenReason(event.target.value)}
              required
              rows={3}
              value={reopenReason}
            />
          </Field>
        ) : null}
      </Dialog>
    </article>
  );
}

function statusLabel(status: SupportTicketStatus): string {
  return status === "WAITING_FOR_SUPPORT" ? "等待客服回复" : status === "WAITING_FOR_USER" ? "等待用户回复" : "已关闭";
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString("zh-CN", { dateStyle: "short", timeStyle: "short" });
}
